/**
 * A button, and the one true reason it exists here.
 *
 * The button classes were being re-typed at every call site, and the variations
 * had already drifted: the invoice editor's primary action, the print button on
 * the document view and the confirm button in a dialog were three different
 * shades of the same blue. A user learns "the coloured one is the action" once,
 * and a screen that invents its own colour teaches them the lesson again.
 *
 * A destructive action is a SEPARATE variant on purpose. It is not
 * `variant="primary" tone="red"`: a delete must never be reachable by a colour
 * the user has already learned to trust for saving.
 */
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '../lib/cn';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'success';
export type ButtonSize = 'sm' | 'md';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variant?: ButtonVariant;
  readonly size?: ButtonSize;
  /** Shows a busy state and blocks further clicks. */
  readonly loading?: boolean;
  /** Content shown while `loading`. Defaults to "Working…". */
  readonly loadingText?: string;
  readonly icon?: ReactNode;
  /** Stretches to the width of its container. */
  readonly block?: boolean;
}

const VARIANT: Record<ButtonVariant, string> = {
  primary: 'bg-brand-500 text-white hover:bg-brand-700 border-transparent',
  secondary: 'bg-slate-800 text-slate-100 hover:bg-slate-700 border-slate-700',
  ghost: 'bg-transparent text-slate-300 hover:bg-slate-800 border-transparent',
  danger: 'bg-red-700 text-white hover:bg-red-600 border-transparent',
  success: 'bg-emerald-700 text-white hover:bg-emerald-600 border-transparent',
};

const SIZE: Record<ButtonSize, string> = {
  sm: 'px-2.5 py-1 text-xs gap-1.5',
  md: 'px-3.5 py-2 text-sm gap-2',
};

const BASE =
  'inline-flex items-center justify-center rounded border font-medium transition-colors ' +
  'focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50';

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'secondary',
    size = 'md',
    loading = false,
    loadingText = 'Working…',
    icon,
    block = false,
    className,
    children,
    disabled,
    type = 'button',
    ...rest
  },
  ref,
) {
  return (
    <button
      {...rest}
      ref={ref}
      // `type="button"` by default: a button inside a form that defaults to
      // "submit" turns every stray click into a submission, and a screen with a
      // Save button and a Cancel button must not make Cancel save.
      type={type}
      disabled={disabled === true || loading}
      aria-busy={loading || undefined}
      className={cn(BASE, VARIANT[variant], SIZE[size], block && 'w-full', className)}
    >
      {icon}
      {loading ? loadingText : children}
    </button>
  );
});
